import { useEffect } from "react";

import { useNavigate, useParams, useSearchParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { buildUrl } from "@/components/helpers/url-utils";
import { useWorkspaceSlug } from "@/components/lib/use-workspace-slug";
import { PageLoadingIndicator } from "@/components/nav/PageLoading";
import { WorkspaceLogo } from "@/components/pages_public/WorkspaceLogo";
import { Header } from "@/components/pieces/onboarding/Header";
import { SITE_NAME, STANDARD_NAV_HEIGHT } from "@/css/constants";
import { usePublicWorkspaceQuery } from "@/graphql";

import { LoginForm } from "./LoginForm";

export function LoginPage() {
  const navigate = useNavigate();
  const workspaceSlug = useWorkspaceSlug();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("nextPath");

  const { data, loading } = usePublicWorkspaceQuery({
    variables: { urlSlug: workspaceSlug ?? "" },
    skip: !workspaceSlug,
  });

  useEffect(() => {
    if (data?.publicWorkspace?.isUsingSso) {
      const currentSearchParams = searchParams.toString();
      const ssoPath = currentSearchParams
        ? `/sso?${currentSearchParams}`
        : "/sso";
      navigate(ssoPath, { replace: true });
    }
  }, [data, navigate, searchParams]);

  // Show loading indicator only when we're actually loading workspace data
  if (workspaceSlug && loading) {
    return <PageLoadingIndicator type="compact" />;
  }

  const signUpLink = buildUrl("/signup", {
    data: params,
  });

  return (
    <>
      <SEOHead
        title={`Login to ${SITE_NAME}`}
        description={`Login to your ${SITE_NAME} account. Unlock your potential. Access top quality coaches at ${SITE_NAME}.`}
        canonicalPath="/login"
      />
      {!workspaceSlug && (
        <>
          <Header />
          <div style={{ height: STANDARD_NAV_HEIGHT }} />
        </>
      )}
      <div className="mt-5 flex justify-around">
        <div className="min-w-[95%] sm:min-w-[400px]">
          {workspaceSlug && (
            <div className="mb-4 text-center">
              <WorkspaceLogo />
            </div>
          )}
          <LoginForm
            nextPath={nextPath}
            signUpLink={!workspaceSlug ? signUpLink : undefined}
          />
        </div>
      </div>
    </>
  );
}
